// src/components/ConfirmationDialog.tsx - FIXED: Remove unused import
import React, { useEffect } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';

const ConfirmationDialog: React.FC = () => {
  const { state } = useAppContext(); // FIXED: Removed unused hideConfirmationDialog
  
  const dialog = state.confirmationDialog;
  
  // Close dialog on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dialog?.isOpen) {
        dialog.onCancel();
      }
    };
    
    if (dialog?.isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [dialog]);
  
  if (!dialog?.isOpen) {
    return null;
  }
  
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      dialog.onCancel();
    }
  };
  
  const getIcon = () => {
    switch (dialog.variant) {
      case 'danger':
        return <AlertTriangle size={24} className="text-red-500" />;
      case 'warning':
        return <AlertTriangle size={24} className="text-orange-500" />;
      case 'info':
      default:
        return <Info size={24} className="text-blue-500" />;
    }
  };
  
  const getVariantStyles = () => {
    switch (dialog.variant) {
      case 'danger':
        return {
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white',
          headerColor: 'text-red-900'
        };
      case 'warning':
        return {
          confirmButton: 'bg-orange-600 hover:bg-orange-700 text-white',
          headerColor: 'text-orange-900'
        };
      case 'info':
      default:
        return {
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white',
          headerColor: 'text-blue-900'
        };
    }
  };
  
  const styles = getVariantStyles();
  
  return (
    <div 
      className="confirmation-dialog-overlay"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div 
        className="confirmation-dialog"
        style={{
          backgroundColor: 'white',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--s5)',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={dialog.onCancel}
          style={{
            position: 'absolute',
            top: 'var(--s4)',
            right: 'var(--s4)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--s1)',
            borderRadius: 'var(--radius-clear-button)',
            color: 'var(--gray-400)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-100)';
            e.currentTarget.style.color = 'var(--gray-700)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
          }}
        >
          <X size={20} />
        </button>
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
          {getIcon()}
          <div style={{ flex: 1 }}>
            <h3 
              className={styles.headerColor}
              style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: '600', 
                lineHeight: '1.25',
                marginBottom: '8px'
              }}
            >
              {dialog.title}
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#6b7280', 
              fontSize: '14px', 
              lineHeight: '1.5' 
            }}>
              {dialog.message}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '24px'
        }}>
          <button
            onClick={dialog.onCancel}
            style={{
              padding: 'var(--s2) var(--s4)',
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-button)',
              backgroundColor: 'white',
              color: 'var(--gray-700)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--gray-50)';
              e.currentTarget.style.borderColor = 'var(--gray-300)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = 'var(--gray-200)';
            }}
          >
            {dialog.cancelText}
          </button>
          <button
            onClick={dialog.onConfirm}
            className={styles.confirmButton}
            style={{
              padding: 'var(--s2) var(--s4)',
              border: 'none',
              borderRadius: 'var(--radius-button)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {dialog.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;